import { type JSX, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { type ConvitePronto, api, explicarMotivo } from '../lib/api.js';

/**
 * Tela do convidado: define a própria senha e entra.
 *
 * PÚBLICA — quem chega aqui ainda não tem sessão. O servidor dá a mesma
 * resposta para token inválido e expirado, então esta tela também não
 * distingue os dois: a instrução é a mesma, pedir um convite novo.
 */
export function AceitarConvite({
  token,
  aoEntrar,
}: {
  token: string;
  aoEntrar: () => void;
}): JSX.Element {
  const [convite, setConvite] = useState<ConvitePronto | null>(null);
  const [invalido, setInvalido] = useState(false);
  const [senha, setSenha] = useState('');
  const [repetida, setRepetida] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    void api
      .get<ConvitePronto>(`/admin/convite/${encodeURIComponent(token)}`)
      .then(setConvite)
      .catch(() => setInvalido(true));
  }, [token]);

  async function definir(): Promise<void> {
    setSalvando(true);
    try {
      await api.post('/admin/convite/aceitar', { token, senha });
      // Já entra logado: o servidor devolveu o cookie de sessão.
      aoEntrar();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível definir a senha'));
    } finally {
      setSalvando(false);
    }
  }

  if (invalido) {
    return (
      <Center mih="100vh" p="md">
        <Paper withBorder shadow="sm" p="xl" w={420}>
          <Stack gap="sm">
            <Title order={1} size="h3">
              Convite não é mais válido
            </Title>
            <Text size="sm">
              Este link já foi usado ou passou do prazo de 48 horas. Nada de errado do seu lado —
              os convites expiram por segurança.
            </Text>
            <Text size="sm">
              <strong>Peça um convite novo</strong> a quem administra o painel do aeroclube. O link
              anterior deixa de valer assim que outro é gerado.
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  if (!convite) {
    return (
      <Center mih="100vh">
        <Group gap="xs">
          <Loader size="sm" />
          <Text c="dimmed">Carregando…</Text>
        </Group>
      </Center>
    );
  }

  const curta = senha.length > 0 && senha.length < 12;
  const naoCoincide = repetida.length > 0 && senha !== repetida;
  const pode = senha.length >= 12 && senha === repetida && !salvando;

  return (
    <Center mih="100vh" p="md">
      <Paper withBorder shadow="sm" p="xl" w={420}>
        <Stack gap="sm">
          <div>
            <Title order={1} size="h3">
              {convite.primeiroAcesso ? 'Bem-vindo' : 'Nova senha'}
            </Title>
            <Text size="sm" c="dimmed">
              {convite.nome} · {convite.email}
            </Text>
          </div>

          <PasswordInput
            label="Sua senha"
            description="Mínimo de 12 caracteres. Uma frase comum de lembrar funciona melhor que uma palavra curta com símbolos."
            value={senha}
            autoComplete="new-password"
            data-autofocus
            error={curta ? `Faltam ${12 - senha.length} caracteres.` : null}
            onChange={(e) => setSenha(e.currentTarget.value)}
          />
          <PasswordInput
            label="Repita a senha"
            value={repetida}
            autoComplete="new-password"
            error={naoCoincide ? 'As duas senhas não são iguais.' : null}
            onChange={(e) => setRepetida(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && pode) void definir();
            }}
          />

          {erro && (
            <Alert color="red" variant="light" role="alert">
              {erro}
            </Alert>
          )}

          <Button variant="filled" loading={salvando} disabled={!pode} onClick={() => void definir()}>
            Definir senha e entrar
          </Button>
        </Stack>
      </Paper>
    </Center>
  );
}
